/**
 * WorkshopDisplay — det enda publika fönstret på HDMI-skärmen.
 * Lyssnar på workshops/{id}.display.mode och byter vy automatiskt.
 * Modes: 'blank' | 'intro' | 'schedule'
 * Kräver ingen inloggning.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Fade, Typography, IconButton, Tooltip } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { db } from '../../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { useCompanyLogo } from '../../../hooks/useQRLogo';
import PublicView from './IntroPresenter/PublicView';
import SchedulePublicView from './IntroPresenter/SchedulePublicView';
import GroupAnalysisPublicView from './GroupAnalysisPublicView';
import GroupResponsesPublicView from './GroupResponsesPublicView';
import WorkshopLiveView from './WorkshopLiveView';

export default function WorkshopDisplay({ sessionId }) {
  const [mode,         setMode]         = useState(null);
  const [title,        setTitle]        = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFsHint,   setShowFsHint]   = useState(true);
  const companyLogo = useCompanyLogo();

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (document.fullscreenElement) setShowFsHint(false);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setMode(d.display?.mode || 'blank');
        setTitle(d.title || '');
      } else {
        setMode('blank');
      }
    });
    return unsub;
  }, [sessionId]);

  if (mode === null) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: '#444' }} />
    </Box>
  );

  // Floating fullscreen button + first-time hint
  const fsBtn = (
    <>
      {showFsHint && !isFullscreen && (
        <Box
          onClick={() => { toggleFullscreen(); setShowFsHint(false); }}
          sx={{
            position: 'fixed', bottom: 56, right: 16, zIndex: 9999,
            bgcolor: 'rgba(0,0,0,0.85)', color: '#fff',
            px: 2, py: 1.25, borderRadius: 2,
            fontSize: '0.78rem', fontWeight: 600,
            cursor: 'pointer', backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', gap: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <FullscreenIcon sx={{ fontSize: 18 }} />
          Klicka för fullskärm
        </Box>
      )}
      <Tooltip title={isFullscreen ? 'Avsluta fullskärm' : 'Fullskärm'}>
        <IconButton
          onClick={toggleFullscreen}
          sx={{
            position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
            bgcolor: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', color: '#fff' },
            backdropFilter: 'blur(4px)',
          }}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Tooltip>
    </>
  );

  if (mode === 'workshop') return (
    <Fade in key="workshop" timeout={400}>
      <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <WorkshopLiveView sessionId={sessionId} />
        </Box>
        {fsBtn}
      </Box>
    </Fade>
  );

  if (mode === 'group-responses') return (
    <Fade in key="group-responses" timeout={400}>
      <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <GroupResponsesPublicView sessionId={sessionId} />
        </Box>
        {fsBtn}
      </Box>
    </Fade>
  );

  if (mode === 'analysis') return (
    <Fade in key="analysis" timeout={400}>
      <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <GroupAnalysisPublicView sessionId={sessionId} />
        </Box>
        {fsBtn}
      </Box>
    </Fade>
  );

  if (mode === 'intro') return (
    <Fade in key="intro" timeout={400}>
      <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <PublicView sessionId={sessionId} />
        </Box>
        {fsBtn}
      </Box>
    </Fade>
  );

  if (mode === 'schedule') return (
    <Fade in key="schedule" timeout={400}>
      <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <SchedulePublicView sessionId={sessionId} />
        </Box>
        {fsBtn}
      </Box>
    </Fade>
  );

  // blank / default — standby-skärm
  return (
    <Fade in key="blank" timeout={600}>
      <Box sx={{
        width: '100vw', height: '100vh',
        bgcolor: '#111',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        position: 'relative',
      }}>
        {companyLogo
          ? <Box component="img" src={companyLogo} alt="" sx={{ maxWidth: 240, maxHeight: 80, objectFit: 'contain', opacity: 0.25 }} />
          : null
        }
        {title && (
          <Typography sx={{ color: '#222', fontSize: '1rem', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase' }}>
            {title}
          </Typography>
        )}
        {fsBtn}
      </Box>
    </Fade>
  );
}
